import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Award, 
  Users, 
  Clock, 
  Shield,
  Heart,
  Target,
  Sparkles,
  Phone,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useContent } from '../contexts/ContentContext';

const LOGO_URL = "/images/pool-service-logo-white.png";

const values = [
  {
    icon: Target,
    title: 'Excellence',
    description: 'We strive for perfection in every pool we service, ensuring crystal clear water every time.'
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: 'Honest pricing, reliable service, and transparent communication are the foundation of our business.'
  },
  {
    icon: Sparkles,
    title: 'Quality',
    description: 'We use only the best products and techniques to maintain and improve your pool.'
  },
  {
    icon: Users,
    title: 'Community',
    description: 'We treat every customer like family and are proud to serve the Tampa Bay community.'
  },
];

export const About = () => {
  const { about, global } = useContent();

  const stats = [
    { value: about.yearsInBusiness, label: about.yearsLabel, icon: Clock },
    { value: about.customersServed, label: about.customersLabel, icon: Users },
    { value: about.satisfactionRate, label: about.satisfactionLabel, icon: Award },
    { value: about.supportHours, label: about.supportLabel, icon: Shield },
  ];

  const milestones = about.milestones || [];

  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About Us | A-Quality Pool Service - A Cody Pools Company | Tampa Bay, FL</title>
        <meta name="description" content="Learn about A-Quality Pool Service, a Cody Pools Company serving Tampa Bay since 2008. Over 15 years of trusted pool care for residential and commercial customers." />
        <link rel="canonical" href="https://a-qualitypools.net/about" />
      </Helmet>
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img loading="lazy" decoding="async"
            src="/images/45_Landry.jpg"
            alt=""
            width={1200}
            height={800}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-sky-400 font-medium mb-4 block">{about.heroTagline}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
              {about.heroHeadline}
            </h1>
            <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto">
              {about.heroSubtext}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-sky-500">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${stat.label?.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <stat.icon className="h-8 w-8 text-white/80 mx-auto mb-2" />
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-white/80">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-sky-500 font-medium mb-2 block">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {about.storyTitle}
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>{about.storyText1}</p>
                <p>{about.storyText2}</p>
                <p>{about.storyText3}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img loading="lazy" decoding="async"
                    src="/images/006-5434-Abington-Creek-Ln-Sugar-Land.jpg"
                    alt="Beautiful Florida pool"
                    width={1200}
                    height={802}
                    className="w-full h-[450px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 bg-white p-6 rounded-2xl shadow-xl">
                  <img loading="lazy" decoding="async"
                    src={LOGO_URL}
                    alt="A-Quality Pool Service"
                    width={320}
                    height={225}
                    className="h-20 w-auto"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-sky-500 font-medium mb-2 block">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {about.valuesTitle}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {about.valuesSubtext}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className="mx-auto w-16 h-16 bg-sky-500/10 rounded-2xl flex items-center justify-center mb-6">
                      <value.icon className="h-8 w-8 text-sky-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      {milestones.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <span className="text-sky-500 font-medium mb-2 block">Our Journey</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Milestones
              </h2>
            </motion.div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-sky-200 -translate-x-1/2" />

              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center mb-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100">
                      <span className="text-sky-500 font-bold text-lg">{milestone.year}</span>
                      <p className="text-slate-700 mt-1">{milestone.event}</p>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 w-4 h-4 bg-sky-500 rounded-full -translate-x-1/2 border-4 border-white shadow" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Us Section */}
      <section className="pt-24 pb-36 bg-sky-500">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-white/80 font-medium mb-2 block">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                The A-Quality Advantage
              </h2>
              <div className="space-y-4">
                {[
                  'Licensed, insured, and background-checked technicians',
                  'Part of the trusted Cody Pools network',
                  'Comprehensive service reports after every visit',
                  'Flexible scheduling to fit your needs',
                  'No long-term contracts required',
                  'Satisfaction guaranteed on all services',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <CheckCircle className="h-6 w-6 text-white mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h3>
                  <p className="text-white/80 mb-6">
                    Join thousands of satisfied customers who trust A-Quality Pool Service 
                    for their pool care needs.
                  </p>
                  <div className="space-y-4">
                    <Button
                      asChild
                      data-testid="about-cta-quote"
                      className="w-full bg-white text-sky-600 hover:bg-slate-100 rounded-full py-6"
                    >
                      <Link to="/contact">Get Your Free Quote</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-2 border-white text-white hover:bg-white hover:text-sky-600 rounded-full py-6 bg-transparent"
                    >
                      <a href={`tel:${global.phone}`}>
                        <Phone className="mr-2 h-5 w-5" />
                        Call {global.phone}
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
